import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/modules/shared/api/axios";

const cleanEntry = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const clone = { ...value };
    delete clone.headers;
    delete clone.config;
    delete clone.request;
    return clone;
  }

  return value;
};

const cleanArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(cleanEntry);
};

export const fetchDashboardAll = createAsyncThunk(
  "dashboard/fetchAll",
  async ({ uuid } = {}, thunkAPI) => {
    try {
      const response = await api.get("/dashboard/summary");
      return response?.data?.data || response?.data || {};
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to load dashboard"
      );
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  tenderCount: {
    TotalTender: 0,
    receivedProposals: 0,
    submittedOffers: 0,
    submittedOffersValue: 0,
    invitationsSent: 0,
    totalActive: 0,
    totalClosed: 0,
    totalBidEvaluation: 0,
    totalAwarded: 0,
    totalCancelled: 0,
  },
  submittedOffers: [],
  receivedProposals: [],
  userOffers: [],
  myTenders: [],
  offersWithDeadlines: [],
  lastFetched: null,
  timestamp: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    hydrateDashboard(state, action) {
      const payload = action.payload || {};
      if (payload.tenderCount) {
        state.tenderCount = { ...state.tenderCount, ...payload.tenderCount };
      }
      if (payload.submittedOffers) {
        state.submittedOffers = cleanArray(payload.submittedOffers);
      }
      if (payload.receivedProposals) {
        state.receivedProposals = cleanArray(payload.receivedProposals);
      }
      if (payload.userOffers) {
        state.userOffers = cleanArray(payload.userOffers);
      }
      if (payload.myTenders) {
        state.myTenders = cleanArray(payload.myTenders);
      }
      if (payload.offersWithDeadlines) {
        state.offersWithDeadlines = cleanArray(payload.offersWithDeadlines);
      }
      if (payload.timestamp) {
        state.timestamp = payload.timestamp;
        state.lastFetched = payload.timestamp;
      }
      state.loading = false;
    },
    clearDashboardError(state) {
      state.error = null;
    },
    resetDashboard() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardAll.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardAll.fulfilled, (state, action) => {
        const data = action.payload || {};
        state.loading = false;
        state.error = null;
        state.tenderCount = { ...state.tenderCount, ...(data.tenderCount || {}) };
        state.submittedOffers = cleanArray(data.submittedOffers);
        state.receivedProposals = cleanArray(data.receivedProposals);
        state.userOffers = cleanArray(data.userOffers);
        state.myTenders = cleanArray(data.myTenders);
        state.offersWithDeadlines = cleanArray(data.offersWithDeadlines);
        state.timestamp = data.timestamp ?? Date.now();
        state.lastFetched = state.timestamp;

        const uuid = action?.meta?.arg?.uuid;
        if (uuid) {
          try {
            const cacheKey = `dashboardCache_v4:${uuid}`;
            localStorage.setItem(
              cacheKey,
              JSON.stringify({
                tenderCount: state.tenderCount,
                submittedOffers: state.submittedOffers,
                receivedProposals: state.receivedProposals,
                userOffers: state.userOffers,
                myTenders: state.myTenders,
                offersWithDeadlines: state.offersWithDeadlines,
                timestamp: state.timestamp,
              })
            );
          } catch (_err) {
            // ignore caching errors
          }
        }
      })
      .addCase(fetchDashboardAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Failed to load dashboard";
      });
  },
});

export const { hydrateDashboard, clearDashboardError, resetDashboard } = dashboardSlice.actions;

export const hydrateDashboardFromCache = (uuid) => (dispatch) => {
  if (!uuid) return;
  const cacheCandidates = [`dashboardCache_v4:${uuid}`, `dashboardCache_v3:${uuid}`];

  for (const key of cacheCandidates) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const cached = JSON.parse(raw);
      dispatch(hydrateDashboard(cached));
      break;
    } catch (_err) {
      // ignore cache parsing issues
    }
  }
};

export default dashboardSlice.reducer;

export const selectDashboard = (state) => state.dashboard;
export const selectDashboardLoading = (state) => state.dashboard.loading;
export const selectDashboardError = (state) => state.dashboard.error;
export const selectDashboardCounts = (state) => state.dashboard.tenderCount;
export const selectSubmittedOffers = (state) => state.dashboard.submittedOffers;
export const selectReceivedProposals = (state) => state.dashboard.receivedProposals;
export const selectUserOffers = (state) => state.dashboard.userOffers;
export const selectMyTenders = (state) => state.dashboard.myTenders;
export const selectOffersWithDeadlines = (state) => state.dashboard.offersWithDeadlines;
